import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { knowledgeApiRef } from '../../../api';
import type {
  DefinitionCompletion,
  DefinitionFieldState,
  DefinitionFieldStatus,
  DefinitionFinding,
  DefinitionSchema,
  KnowledgeMessage,
} from '../../../api';
import { useKnowledgeBuilder } from '../KnowledgeBuilderProvider';

export type SectionDotStatus =
  | 'complete'
  | 'current'
  | 'hasFindings'
  | 'pending'
  | 'unresolved';

interface DefinitionInterviewContextValue {
  schema?: DefinitionSchema;
  fields: DefinitionFieldState[];
  completion: DefinitionCompletion;
  findings: DefinitionFinding[];
  messages: KnowledgeMessage[];
  activeSectionId?: string;
  isReviewSelected: boolean;
  isSending: boolean;
  isReviewing: boolean;
  reviewMessage?: string;
  loading: boolean;
  error?: string;
  selectSection(sectionId: string): void;
  selectReview(): void;
  sendInterviewMessage(
    content: string,
    attachments?: unknown,
  ): Promise<void>;
  abortInterviewMessage(): void;
  setFieldStatus(
    sectionId: string,
    fieldId: string,
    status: DefinitionFieldStatus,
    value?: string,
  ): Promise<void>;
  runReview(): Promise<void>;
  fieldFor(sectionId: string, fieldId: string): DefinitionFieldState | undefined;
  sectionStatus(sectionId: string): SectionDotStatus;
}

const DefinitionInterviewContext = createContext<
  DefinitionInterviewContextValue | undefined
>(undefined);

export function useDefinitionInterview() {
  const value = useContext(DefinitionInterviewContext);
  if (!value) {
    throw new Error(
      'useDefinitionInterview must be used inside DefinitionInterviewProvider',
    );
  }
  return value;
}

export const DefinitionInterviewProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const api = useApi(knowledgeApiRef);
  const { session, selectDocument, refreshSessionState } =
    useKnowledgeBuilder();
  const [schema, setSchema] = useState<DefinitionSchema>();
  const [fields, setFields] = useState<DefinitionFieldState[]>([]);
  const [completion, setCompletion] = useState<DefinitionCompletion>({
    confirmedRequired: 0,
    totalRequired: 0,
    percent: 100,
  });
  const [findings, setFindings] = useState<DefinitionFinding[]>([]);
  const [messages, setMessages] = useState<KnowledgeMessage[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string>();
  const [isReviewSelected, setIsReviewSelected] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewMessage, setReviewMessage] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const activeRequest = useRef<AbortController>();

  useEffect(() => {
    let cancelled = false;
    if (!session?.id) {
      return undefined;
    }
    (async () => {
      try {
        const state = await api.getDefinitionState(session.id);
        if (cancelled) {
          return;
        }
        setSchema(state.schema);
        setFields(state.fields);
        setCompletion(state.completion);
        setFindings(state.findings);
        setActiveSectionId(firstIncompleteSection(state.schema, state.fields));
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Failed to load the definition',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, session?.id]);

  useEffect(() => {
    if (!schema || !activeSectionId) {
      return;
    }
    const section = schema.sections.find(item => item.id === activeSectionId);
    const target = section?.fields.find(field => field.targetDocument)
      ?.targetDocument;
    if (target) {
      selectDocument(target);
    }
  }, [schema, activeSectionId, selectDocument]);

  const selectSection = useCallback((sectionId: string) => {
    setIsReviewSelected(false);
    setActiveSectionId(sectionId);
  }, []);

  const selectReview = useCallback(() => {
    setIsReviewSelected(true);
  }, []);

  const sendInterviewMessage = useCallback(
    async (content: string) => {
      if (!session?.id || !content.trim()) {
        return;
      }
      setIsSending(true);
      setError(undefined);
      const userMessage: KnowledgeMessage = {
        id: `local-${Date.now()}`,
        sessionId: session.id,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages(current => [...current, userMessage]);
      const activeTargetDocument = schema?.sections
        .find(section => section.id === activeSectionId)
        ?.fields.find(field => field.targetDocument)?.targetDocument;
      const controller = new AbortController();
      activeRequest.current = controller;
      try {
        const response = await api.sendInterviewMessage(
          session.id,
          content,
          activeTargetDocument,
          undefined,
          controller.signal,
        );
        setMessages(current => [...current, response.message]);
        if (response.definitionState) {
          setFields(response.definitionState.fields);
          setCompletion(response.definitionState.completion);
          setFindings(response.definitionState.findings);
        }
        await refreshSessionState();
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : 'Failed to send message');
        }
      } finally {
        setIsSending(false);
        activeRequest.current = undefined;
      }
    },
    [api, session?.id, schema, activeSectionId, refreshSessionState],
  );

  const abortInterviewMessage = useCallback(() => {
    activeRequest.current?.abort();
  }, []);

  const autoStarted = useRef(false);
  useEffect(() => {
    if (
      autoStarted.current ||
      loading ||
      !schema ||
      messages.length > 0 ||
      isSending
    ) {
      return;
    }
    autoStarted.current = true;
    void sendInterviewMessage('Start interview');
  }, [loading, schema, messages.length, isSending, sendInterviewMessage]);

  const setFieldStatus = useCallback(
    async (
      sectionId: string,
      fieldId: string,
      status: DefinitionFieldStatus,
      value?: string,
    ) => {
      if (!session?.id) {
        return;
      }
      const field = await api.setDefinitionFieldStatus(
        session.id,
        sectionId,
        fieldId,
        status,
        value,
      );
      setFields(current => [
        ...current.filter(
          item => !(item.sectionId === sectionId && item.fieldId === fieldId),
        ),
        field,
      ]);
    },
    [api, session?.id],
  );

  const runReview = useCallback(async () => {
    if (!session?.id) {
      return;
    }
    setIsReviewing(true);
    setError(undefined);
    try {
      const result = await api.runDefinitionReview(session.id);
      setFindings(result.findings);
      setReviewMessage(result.message);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run the review');
    } finally {
      setIsReviewing(false);
    }
  }, [api, session?.id]);

  const fieldFor = useCallback(
    (sectionId: string, fieldId: string) =>
      fields.find(
        field => field.sectionId === sectionId && field.fieldId === fieldId,
      ),
    [fields],
  );

  const sectionStatus = useCallback(
    (sectionId: string): SectionDotStatus => {
      const section = schema?.sections.find(item => item.id === sectionId);
      if (!section) {
        return 'pending';
      }
      const requiredFields = section.fields.filter(field => field.required);
      // A section with no required fields would vacuously satisfy `every()`
      // on an empty array before anything was ever answered, so fall back to
      // treating every field as relevant — the dot then starts empty and
      // only fills in once the (optional) fields are actually addressed.
      const relevantFields = requiredFields.length
        ? requiredFields
        : section.fields;
      const allResolved =
        relevantFields.length > 0 &&
        relevantFields.every(field => {
          const status = fieldFor(sectionId, field.id)?.status;
          return status === 'confirmed' || status === 'deferred';
        });
      const hasDeferred = relevantFields.some(
        field => fieldFor(sectionId, field.id)?.status === 'deferred',
      );
      const hasBlockingFinding = findings.some(
        finding =>
          finding.sectionId === sectionId &&
          !finding.resolved &&
          (finding.severity === 'ERROR' || finding.severity === 'WARNING'),
      );
      if (hasBlockingFinding) {
        return 'hasFindings';
      }
      if (allResolved && hasDeferred) {
        return 'unresolved';
      }
      if (allResolved) {
        return 'complete';
      }
      if (sectionId === activeSectionId && !isReviewSelected) {
        return 'current';
      }
      return 'pending';
    },
    [schema, fieldFor, findings, activeSectionId, isReviewSelected],
  );

  const value = useMemo<DefinitionInterviewContextValue>(
    () => ({
      schema,
      fields,
      completion,
      findings,
      messages,
      activeSectionId,
      isReviewSelected,
      isSending,
      isReviewing,
      reviewMessage,
      loading,
      error,
      selectSection,
      selectReview,
      sendInterviewMessage,
      abortInterviewMessage,
      setFieldStatus,
      runReview,
      fieldFor,
      sectionStatus,
    }),
    [
      schema,
      fields,
      completion,
      findings,
      messages,
      activeSectionId,
      isReviewSelected,
      isSending,
      isReviewing,
      reviewMessage,
      loading,
      error,
      selectSection,
      selectReview,
      sendInterviewMessage,
      abortInterviewMessage,
      setFieldStatus,
      runReview,
      fieldFor,
      sectionStatus,
    ],
  );

  return (
    <DefinitionInterviewContext.Provider value={value}>
      {children}
    </DefinitionInterviewContext.Provider>
  );
};

function firstIncompleteSection(
  schema?: DefinitionSchema,
  fields: DefinitionFieldState[] = [],
) {
  if (!schema) {
    return undefined;
  }
  for (const section of schema.sections) {
    const requiredFields = section.fields.filter(field => field.required);
    const allConfirmed = requiredFields.every(
      field =>
        fields.find(
          item => item.sectionId === section.id && item.fieldId === field.id,
        )?.status === 'confirmed',
    );
    if (!allConfirmed) {
      return section.id;
    }
  }
  return schema.sections[schema.sections.length - 1]?.id;
}
